import * as Sentry from '@sentry/react';
import { isPreviewFormTypeLoadInFlight } from 'donation-elements/src/Util/formTypeModules';

// Recover from stale-chunk failures after a prod deploy. Every dashboard route
// is lazy-loaded (React.lazy in App.jsx); a deploy replaces the content-hashed
// chunks, so a session opened before the deploy fetches a chunk URL that no
// longer exists, the SPA fallback answers with index.html, and the module
// loader throws (Safari: "'text/html' is not a valid JavaScript MIME type.").
// Vite emits vite:preloadError for exactly this case — reload once to pick up
// the new bundle. The timestamp guard stops a reload loop when the failure is
// NOT deploy-related (e.g. a captive portal answering every request with HTML):
// repeat failures within the window fall through to DashboardErrorBoundary,
// whose Reload button does a real page reload.
export const CHUNK_RELOAD_AT_KEY = 'cs-dashboard-chunk-reload-at';
export const CHUNK_RELOAD_MIN_INTERVAL_MS = 60_000;

export function handleChunkPreloadError(
  event,
  { sentry = Sentry, reload = () => window.location.reload() } = {},
) {
  // CON-338-G: while the form editor preview loads a donation-elements form-type
  // chunk, a failure is the preview's to handle (in-place retry, then a local
  // error). Reloading here would throw away the nonprofit's unsaved edits, and
  // claiming the event would hand the loader `undefined` instead of the error.
  // Safari's import error carries no chunk URL, so this is a time window, not a
  // URL match: a route chunk failing inside it reaches DashboardErrorBoundary.
  if (isPreviewFormTypeLoadInFlight()) return;
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY)) || 0;
  } catch {
    // sessionStorage unavailable (privacy mode) — treat as never reloaded;
    // worst case the guard below can't persist and we reload on each failure,
    // which still beats a dead dashboard.
  }
  if (Date.now() - lastReloadAt < CHUNK_RELOAD_MIN_INTERVAL_MS) {
    return;
  }
  try {
    sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(Date.now()));
  } catch {
    return; // can't guard against a loop — let the error boundary handle it
  }
  event.preventDefault();
  // Make the SUCCESSFUL auto-recovery visible: without this, only the sessions
  // that fall through to the error boundary ever reach Sentry, so a deploy that
  // silently reloads thousands of tabs looks identical to one that reloads
  // none. Best-effort — the reload must never be blocked beyond the flush
  // timeout, and a missing DSN / dev mode (no Sentry client) must not break
  // the recovery at all.
  let flushed = null;
  try {
    if (sentry.getClient()) {
      sentry.captureMessage('dashboard chunk stale auto-reload', 'info');
      flushed = sentry.flush(2000);
    }
  } catch {
    // Sentry unavailable or flush threw synchronously — reload anyway.
  }
  if (flushed && typeof flushed.finally === 'function') {
    flushed.finally(() => reload());
  } else {
    reload();
  }
}

// Installs the policy on `window`; returns a disposer (tests).
export function installChunkReloadListener(options) {
  const listener = (event) => handleChunkPreloadError(event, options);
  window.addEventListener('vite:preloadError', listener);
  return () => window.removeEventListener('vite:preloadError', listener);
}
